<script setup lang="ts">
import { addMoney, formatMonthYear, isoWeekNumber, isoWeekYear, isZeroMoney, zeroMoney } from '@taler/domain'
import type { LocalDate, Money } from '@taler/domain'
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { plainAmount, useAccountingView } from '@/app/accounting-view'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { PeriodRollupTableData } from '../internal/period-rollup-table.ts'
import type { PERIOD_ROLLUP_BUCKETS } from '../internal/query-params.ts'

const props = defineProps<{
  data: PeriodRollupTableData
  bucket: (typeof PERIOD_ROLLUP_BUCKETS)[number]
  accountClass: string
}>()
const emit = defineEmits<{ toggle: [accountId: string] }>()
const { t, locale } = useI18n()
const localeCode = computed(() => locale.value as 'en' | 'hu')
const totalHeaderRows = computed(() => Math.max(props.data.headerRows.length, 1))
const currency = computed(() => props.data.rows[0]?.total.currency)
const grandTotal = computed(() => {
  const first = props.data.rows[0]
  if (first === undefined) return undefined
  return props.data.rows.reduce((sum, row) => addMoney(sum, row.total), zeroMoney(first.total.currency))
})
const { accountingView } = useAccountingView()

function shown(value: Money): Money {
  return plainAmount(value, props.accountClass, accountingView.value)
}

function periodLabel(periodStart: LocalDate): string {
  if (props.bucket === 'WEEK') {
    return t('reporting.bucketColumnWeek', {
      week: isoWeekNumber(periodStart),
      year: isoWeekYear(periodStart),
    })
  }
  return formatMonthYear(periodStart, localeCode.value)
}

/** The display currency is stated once above the table, so a figure carries only its digits. */
function figure(money: Money): { money: Money; zero: boolean } {
  return { money, zero: isZeroMoney(money) }
}

const figureRows = computed(() =>
  props.data.rows.map((row) => ({
    periodStart: row.periodStart,
    cells: row.cells.map(figure),
    total: figure(row.total),
  })),
)

const VALUE_COLUMN = 'px-[var(--space-inline)]'
const TOTAL_TINT = 'bg-[color:color-mix(in_srgb,var(--color-accent)_7%,var(--color-surface))]'
const HEADER_TEXT = 'font-semibold text-[length:var(--text-step-2)] text-[color:var(--color-text)]'
const SUBHEADER_TEXT = 'font-normal text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]'
</script>

<template>
  <section
    class="flex min-w-0 flex-col rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)]"
  >
    <header
      v-if="currency"
      class="flex flex-wrap items-center justify-between gap-x-[var(--space-block)] gap-y-[var(--space-inline)] border-b border-[color:var(--color-border)] px-[var(--space-group)] py-[var(--space-inline)] text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
    >
      <p>{{ t('reporting.pivotAmountsIn', { currency }) }}</p>
      <p v-if="grandTotal" class="flex flex-wrap items-center gap-x-[var(--space-inline)] tabular-nums">
        <span>{{ t('reporting.periodCount', { count: data.rows.length }, data.rows.length) }}</span>
        <span aria-hidden="true">•</span>
        <span>{{ t('reporting.categoryCount', { count: data.categoryCount }, data.categoryCount) }}</span>
        <span aria-hidden="true">•</span>
        <MoneyAmount :value="shown(grandTotal)" class="text-[color:var(--color-text)]" />
        <span>{{ t('reporting.periodRollupTotalSuffix') }}</span>
      </p>
    </header>
    <div
      tabindex="0"
      role="region"
      :aria-label="t('reporting.periodRollupTitle')"
      class="min-w-0 max-w-full overflow-x-auto"
    >
      <table class="w-max border-collapse">
        <thead>
          <tr
            v-for="(headerRow, rowIndex) in data.headerRows"
            :key="rowIndex"
            class="border-b border-[color:var(--color-border)]"
          >
            <th
              v-if="rowIndex === 0"
              scope="col"
              :rowspan="totalHeaderRows"
              class="sticky left-0 z-10 min-w-[9rem] whitespace-nowrap border-r border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-left align-middle"
              :class="HEADER_TEXT"
            >
              {{ t('reporting.periodRollupPeriodColumn') }}
            </th>
            <th
              v-for="cell in headerRow"
              :key="cell.key"
              scope="col"
              :colspan="cell.colSpan"
              :rowspan="cell.rowSpan"
              :data-value="cell.colSpan === 1 ? '' : undefined"
              class="align-top"
              :class="[
                VALUE_COLUMN,
                cell.kind === 'group'
                  ? 'border-b-2 border-[color:var(--color-accent)] text-center'
                  : 'text-right',
                cell.kind === 'group' || cell.rowSpan > 1 ? HEADER_TEXT : SUBHEADER_TEXT,
              ]"
            >
              <div
                :class="
                  cell.rowSpan > 1
                    ? 'flex h-full flex-col justify-between gap-[var(--space-inline)] py-[var(--space-inline)]'
                    : 'contents'
                "
              >
                <button
                  v-if="cell.hasChildren"
                  type="button"
                  class="inline-flex min-h-[var(--density-target-min)] items-center gap-1"
                  :class="cell.rowSpan > 1 ? undefined : 'py-[var(--space-inline)]'"
                  :aria-expanded="cell.kind === 'group'"
                  @click="emit('toggle', cell.accountId!)"
                >
                  <span class="max-w-[9rem] truncate" :title="cell.label">{{ cell.label }}</span>
                  <svg
                    aria-hidden="true"
                    width="12"
                    height="12"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    :class="cell.kind === 'group' ? 'rotate-180' : undefined"
                  >
                    <path d="M4 6l4 4 4-4" />
                  </svg>
                </button>
                <span
                  v-else
                  class="block max-w-[9rem] truncate"
                  :class="cell.rowSpan > 1 ? undefined : 'py-[var(--space-inline)]'"
                  :title="cell.label"
                >
                  {{ cell.kind === 'other' ? t('reporting.other') : cell.label }}
                </span>
                <span v-if="cell.rowSpan > 1" :class="SUBHEADER_TEXT">
                  {{ t('reporting.pivotTotalRow') }}
                </span>
              </div>
            </th>
            <th
              v-if="rowIndex === 0"
              scope="col"
              :rowspan="totalHeaderRows"
              data-value
              class="sticky right-0 z-10 border-l border-[color:var(--color-border)] bg-[color:var(--color-surface)] align-middle"
              :class="[VALUE_COLUMN, 'text-right', TOTAL_TINT, HEADER_TEXT]"
            >
              {{ t('reporting.pivotTotalRow') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in figureRows"
            :key="row.periodStart.epochDay"
            class="h-[var(--density-row-compact)] border-b border-[color:var(--color-border)] last:border-0 hover:bg-[color:var(--color-surface-raised)]"
          >
            <th
              scope="row"
              class="sticky left-0 z-10 min-w-[9rem] whitespace-nowrap border-r border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-left text-[length:var(--text-step-2)] font-normal text-[color:var(--color-text)]"
            >
              {{ periodLabel(row.periodStart) }}
            </th>
            <td
              v-for="(cell, index) in row.cells"
              :key="index"
              data-value
              class="whitespace-nowrap text-right tabular-nums"
              :class="VALUE_COLUMN"
            >
              <span v-if="cell.zero" class="text-[color:var(--color-text-muted)]">
                <span class="sr-only">0</span>
                <span aria-hidden="true">–</span>
              </span>
              <MoneyAmount v-else :value="shown(cell.money)" hide-currency size="row" weight="regular" />
            </td>
            <td
              data-value
              class="sticky right-0 z-10 whitespace-nowrap border-l border-[color:var(--color-border)] text-right font-semibold tabular-nums text-[color:var(--color-text)]"
              :class="[VALUE_COLUMN, TOTAL_TINT]"
            >
              <span v-if="row.total.zero" class="text-[color:var(--color-text-muted)]">
                <span class="sr-only">0</span>
                <span aria-hidden="true">–</span>
              </span>
              <MoneyAmount v-else :value="shown(row.total.money)" hide-currency size="row" />
            </td>
          </tr>
          <tr v-if="data.rows.length === 0">
            <td :colspan="data.leafCount + 2" class="px-[var(--space-cell)] py-[var(--space-block)]">
              <p class="text-[color:var(--color-text-muted)]">{{ t('reporting.pivotEmpty') }}</p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>
